import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Heart, Loader2, Sparkles, Zap, AlertCircle } from 'lucide-react';
import { Button } from '../../ui/button';
import { useTranslation } from '../../../lib/i18n';
import { videoAPI } from '../../../lib/api';
import SwipeCard, { SwipeActionBar } from '../SwipeCard';

/**
 * Step 3 of the onboarding wizard – "pick your style" swipe deck.
 *
 * Drives the deck from the catalog `demo_video` URLs returned by
 * `GET /api/templates`. We used to fire a fresh Creatomate render per
 * new user against their first property. Good for "wow this is *my*
 * house" but expensive (5–14 paid renders per signup) and made the
 * first swipe card take 1–4 min to appear. Switching to the catalog
 * showcase videos makes the first card interactive in ~100 ms and
 * costs zero Creatomate credits per signup. Per-property rendering
 * can come back later as an opt-in "see with my photos" button.
 *
 * Votes stay client-side: they update the wizard's `likedTemplates` /
 * `dislikedTemplates` arrays directly. The `ActivateStep` already
 * commits those into `AutoPostConfig.favorite_templates` at the
 * finish-line save, so no extra backend plumbing is needed.
 *
 * Props
 *   likedTemplates         string[]  . Template ids already liked
 *   dislikedTemplates      string[]  . Template ids already disliked
 *   onLikedChange(next)    parent setter
 *   onDislikedChange(next) parent setter
 *   isFreeTier             boolean  . Filters to the curated 5 ids
 *   onSkip()               advance to step 4 without finishing the deck
 */
// Curated deck shown to free-tier users. MUST mirror the backend's
// FREE_TIER_SAMPLER (onboarding_samples.py) so the free/pro split behaves
// the same. Spread: bold poster / dark luxury / magazine cover / dark
// glass / cream editorial / agent portrait — upgrade nudge points at the rest.
const FREE_TIER_IDS = [
  'tpl-plakat',
  'tpl-noir-luxus',
  'tpl-cover-editorial',
  'tpl-glass-panel',
  'tpl-nordic-minimal',
  'tpl-agent-story',
];

export default function SampleSwipeStep({
  likedTemplates = [],
  dislikedTemplates = [],
  onLikedChange,
  onDislikedChange,
  isFreeTier = true,
  onSkip,
}) {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [queueIndex, setQueueIndex] = useState(0);
  const [history, setHistory] = useState([]);

  // One-shot catalog fetch on mount. Each template row becomes a card.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await videoAPI.getTemplates();
        if (cancelled) return;
        let rows = Array.isArray(res.data) ? res.data : [];
        if (isFreeTier) rows = rows.filter((r) => FREE_TIER_IDS.includes(r.id));
        // Sort so templates WITH a rendered demo appear first. Anything
        // that's temporarily missing a demo_video drops to the end and
        // plays the generic placeholder state in SwipeCard.
        rows.sort((a, b) => Number(!!b.demo_video) - Number(!!a.demo_video));
        const cards = rows.map((r) => ({
          sample_id: r.id,                 // reuse template id as vote key
          template_id: r.id,
          template_name: r.name,
          has_voiceover: !!r.has_voiceover,
          media_count: r.media_count,
          media_type: r.media_type,        // drives "Fotos" vs "Videos" chip
          // SwipeCard looks at `status` + `video_url` to decide which
          // layer to show. Treat the catalog demo as an already-succeeded
          // render so the card plays the video immediately.
          status: r.demo_video ? 'succeeded' : 'pending',
          video_url: r.demo_video || null,
          demo_video_fallback: r.demo_video || null,
        }));
        setTemplates(cards);
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isFreeTier]);

  // Skip past already-voted templates so stepping back from step 4 and
  // returning doesn't make the user re-swipe cards they've decided on.
  useEffect(() => {
    if (templates.length === 0) return;
    const voted = new Set([...likedTemplates, ...dislikedTemplates]);
    const firstUnvoted = templates.findIndex((c) => !voted.has(c.template_id));
    setQueueIndex(firstUnvoted === -1 ? templates.length : firstUnvoted);
    // intentionally only runs when the templates list loads; we don't
    // want every vote tick to reset our index.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates]);

  const topSample = templates[queueIndex] || null;
  // Just one peek of "more coming" behind the top card. Stacking two cards
  // behind made the deck look noisy and the second card poked out below
  // the top. One is enough to convey there's more without clutter.
  const queueTail = templates.slice(queueIndex + 1, queueIndex + 2);
  const likesCount = likedTemplates.length;
  const dislikesCount = dislikedTemplates.length;
  const done = templates.length > 0 && queueIndex >= templates.length;

  const recordVote = (sample, verdict) => {
    const tpl = sample.template_id;
    setHistory((h) => [...h, { id: tpl, verdict }]);
    setQueueIndex((i) => i + 1);
    if (verdict === 'like') {
      if (!likedTemplates.includes(tpl)) onLikedChange?.([...likedTemplates, tpl]);
      if (dislikedTemplates.includes(tpl)) onDislikedChange?.(dislikedTemplates.filter((x) => x !== tpl));
    } else {
      if (!dislikedTemplates.includes(tpl)) onDislikedChange?.([...dislikedTemplates, tpl]);
      if (likedTemplates.includes(tpl)) onLikedChange?.(likedTemplates.filter((x) => x !== tpl));
    }
  };

  const undoLast = () => {
    const last = history[history.length - 1];
    if (!last) return;
    setHistory((h) => h.slice(0, -1));
    setQueueIndex((i) => Math.max(0, i - 1));
    // Clean the last vote from both arrays so re-swipe lands fresh.
    onLikedChange?.(likedTemplates.filter((x) => x !== last.id));
    onDislikedChange?.(dislikedTemplates.filter((x) => x !== last.id));
  };

  const handleSkipStep = () => onSkip?.();

  // ── UI states ───────────────────────────────────────────────────────────

  if (loading) {
    return (
      <StatusCard
        icon={<Loader2 className="w-10 h-10 text-[#1f4d3a] animate-spin" />}
        title={t('onboarding.step3.preparing') || 'Vorlagen werden geladen…'}
        body={t('onboarding.step3.preparingDesc') || 'Einen Moment. Gleich kannst du loslegen.'}
      />
    );
  }

  if (error || templates.length === 0) {
    return (
      <StatusCard
        icon={<AlertCircle className="w-10 h-10 text-[#b3543f]" />}
        title={t('onboarding.step3.startFailedTitle') || 'Konnte nicht starten'}
        body={t('onboarding.step3.startFailedBody') || 'Wir konnten die Vorlagen nicht laden. Probiere es bitte gleich nochmal.'}
        action={
          <Button onClick={() => window.location.reload()} className="bg-[#1f4d3a] hover:bg-[#163a2c]">
            {t('common.retry') || 'Erneut versuchen'}
          </Button>
        }
      />
    );
  }

  if (done) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="inline-flex p-4 bg-[#d6e8dd] rounded-2xl mb-4">
            <Sparkles className="w-10 h-10 text-[#1e7a4f]" />
          </div>
          <h2 className="text-2xl font-bold text-[#23211a] mb-2">
            {t('onboarding.step3.doneTitle') || 'Fertig geswipt!'}
          </h2>
          <p className="text-[#6f6a5c]">
            {t('onboarding.step3.doneSubtitle', { count: likesCount }) ||
              `Du hast ${likesCount} Vorlagen geliked. Wir nehmen die für deine Auto-Posts.`}
          </p>
        </div>

        <div className="rounded-lg border border-[#e3dccb] bg-[#fffdf7] p-5 space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-[#1e7a4f] flex items-center gap-2">
              <Heart className="w-4 h-4 fill-[#1e7a4f]" />
              {likesCount} Likes
            </span>
            <span className="text-[#8a8474]">{dislikesCount} Dislikes</span>
          </div>
          {likesCount === 0 && (
            <p className="text-xs text-[#8a8474]">
              {t('onboarding.step3.noLikesHint') ||
                'Keine Sorge. Wenn du nichts likst, wählt unsere KI automatisch eine passende Vorlage pro Auto-Post aus. Du kannst später jederzeit ändern, welche Vorlagen du magst.'}
            </p>
          )}
        </div>
      </div>
    );
  }

  // ── Main swipe deck ─────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-[#23211a] mb-1">
          {t('onboarding.step3.title') || 'Wähle deinen Stil'}
        </h2>
        <p className="text-sm text-[#6f6a5c]">
          {t('onboarding.step3.subtitle') ||
            'Hier sind echte Beispiele jeder Vorlage. Like die, die dir gefallen. Wir nutzen sie für deine Auto-Posts.'}
        </p>
      </div>

      {isFreeTier && (
        <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-[#1f4d3a]/30 bg-[#e9efe7]">
          <div className="flex items-center gap-2 text-sm text-[#2e6b50]">
            <Zap className="w-4 h-4 text-[#1f4d3a]" />
            <span>
              {t('onboarding.step3.freeTierBanner') ||
                `Du testest ${templates.length} Vorlagen. Upgrade, um alle zu sehen.`}
            </span>
          </div>
          <a
            href="/pricing"
            className="text-xs font-medium text-[#2e6b50] hover:text-[#23211a] border border-[#1f4d3a]/50 rounded-full px-3 py-1"
          >
            Upgrade
          </a>
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-[#6f6a5c]">
        <span>
          {queueIndex + 1} / {templates.length}
        </span>
        <span className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1 text-[#1e7a4f]">
            <Heart className="w-3 h-3 fill-[#1e7a4f]" />
            {likesCount}
          </span>
          <span>×  {dislikesCount}</span>
        </span>
      </div>
      <div className="relative h-1.5 rounded-full bg-[#e9e2d2] overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 bg-[#1f4d3a] transition-all duration-500"
          style={{ width: `${(queueIndex / Math.max(templates.length, 1)) * 100}%` }}
        />
      </div>

      <div className="relative mx-auto w-full max-w-xs aspect-[9/16]">
        <AnimatePresence initial={false}>
          {queueTail.map((s, i) => (
            <SwipeCard
              key={s.sample_id}
              sample={s}
              position="queue"
              depth={queueTail.length - i}
            />
          ))}
          {topSample && (
            <SwipeCard
              key={topSample.sample_id}
              sample={topSample}
              position="top"
              onSwipe={(verdict) => recordVote(topSample, verdict)}
            />
          )}
        </AnimatePresence>
      </div>

      {topSample && (
        <SwipeActionBar
          onDislike={() => recordVote(topSample, 'dislike')}
          onLike={() => recordVote(topSample, 'like')}
          onUndo={history.length > 0 ? undoLast : null}
          canUndo={history.length > 0}
        />
      )}

      <div className="text-center pt-2">
        <button
          type="button"
          onClick={handleSkipStep}
          className="text-xs text-[#8a8474] hover:text-[#4a463b] underline-offset-2 hover:underline"
        >
          {t('onboarding.step3.skipStep') || 'Diesen Schritt überspringen'}
        </button>
      </div>
    </div>
  );
}

function StatusCard({ icon, title, body, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 gap-3 text-center px-4">
      {icon}
      <h3 className="text-lg font-semibold text-[#23211a]">{title}</h3>
      <p className="text-sm text-[#6f6a5c] max-w-sm">{body}</p>
      {action && <div className="pt-2">{action}</div>}
    </div>
  );
}
